import { Text, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { addMonths, format } from 'date-fns';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { number, object, string } from 'yup';

import Modal from '../../../../../../components/Modal';
import { Input, NumberInput } from '../../../../../../components/formComponents';
import Button from '../../../../../../components/ui/buttons/Button';
import { getLeadByExternalIdQueryKey } from '../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { usePatchLeadByExternalIdData } from '../../../../../../services/marketplace/generated/kubb/hooks/usePatchLeadByExternalIdData';
import { getOfferErrorMessage } from '../offers/utils';

const MAX_MONTHLY_PAYMENT = 5000;

const leaseDetailsSchema = object({
  monthlyPayment: number()
    .typeError('Enter your monthly payment as a number.')
    .required('Enter your monthly payment.')
    .positive('Enter a monthly payment between $1 and $5,000.')
    .max(MAX_MONTHLY_PAYMENT, 'Enter a monthly payment between $1 and $5,000.'),
  leaseEndMonth: string().required('Pick your lease-end month.'),
});

type LeaseDetailsFormValues = {
  monthlyPayment: number | '';
  leaseEndMonth: string;
};

type EditLeaseDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  externalId: string;
  monthlyPayment: number | null;
  remainingMonths: number | null;
};

const EditLeaseDetailsModal = ({
  isOpen,
  onClose,
  externalId,
  monthlyPayment,
  remainingMonths,
}: EditLeaseDetailsModalProps) => {
  const [saveError, setSaveError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { mutateAsync: patchLead, isPending } = usePatchLeadByExternalIdData();

  const handleSave = async (values: LeaseDetailsFormValues) => {
    setSaveError(null);
    try {
      const updatedLead = await patchLead({
        externalId,
        data: {
          listVsBuyoutInputs: {
            monthlyPayment: Number(values.monthlyPayment),
            leaseEndDate: `${values.leaseEndMonth}-01`,
          },
        },
      });
      queryClient.setQueryData(getLeadByExternalIdQueryKey({ externalId }), updatedLead);
      onClose();
    } catch (error) {
      setSaveError(
        getOfferErrorMessage(error) ?? "We couldn't save your details. Please try again.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Your lease details"
      titleFontSize={{ base: '22px', md: '26px' }}
      titleFontWeight="700"
      _content={{ maxW: '440px' }}
      _body={{ pb: '28px' }}
    >
      <Formik<LeaseDetailsFormValues>
        initialValues={{
          monthlyPayment: monthlyPayment ?? '',
          leaseEndMonth:
            remainingMonths != null
              ? format(addMonths(new Date(), remainingMonths), 'yyyy-MM')
              : '',
        }}
        validationSchema={leaseDetailsSchema}
        onSubmit={handleSave}
      >
        <Form>
          <VStack align="stretch" spacing="16px">
            <NumberInput name="monthlyPayment" label="Monthly payment" placeholder="655" isMoney />
            <Input name="leaseEndMonth" type="month" label="Lease-end date" />
            {saveError != null && (
              <Text fontSize="12.5px" color="leaseEndRed">
                {saveError}
              </Text>
            )}
            <Button type="submit" isLoading={isPending} size="sm" w="100%" h="40px">
              Save my details
            </Button>
          </VStack>
        </Form>
      </Formik>
    </Modal>
  );
};

export default EditLeaseDetailsModal;
